// kernel/useTeams.ts
import axios from 'axios';
import { useCallback } from 'react';
import { isAuthorizedForOrganisation } from '#lib/organisation-teams/team-access';
import { mergeOrganisationTeams } from '#lib/organisation-teams/team-rules';
import type {
  KernelHandle,
  LeasingUserDTO,
  OrganisationTeamDTO,
  useTeamsReturnType,
} from '#types/types';

interface useTeamsArgs {
  kernel: React.RefObject<KernelHandle | null>;
}

export const useTeams = ({ kernel }: useTeamsArgs): useTeamsReturnType => {
  const canManageTeams = useCallback<useTeamsReturnType['canManageTeams']>((organisationId) => {
    const normalizedOrgId = typeof organisationId === 'string' ? organisationId.trim() : '';
    if (!normalizedOrgId) return false;
    const userContext = kernel.current?.getState().userContext;
    if (!userContext) return false;
    return isAuthorizedForOrganisation({
      isFaterAdmin: userContext.isFaterAdmin,
      isOrganisationAdmin: userContext.isOrganisationAdmin,
      userOrgId: userContext.organisationId,
    }, normalizedOrgId);
  }, [kernel]);

  const getOrganisationTeams = useCallback<useTeamsReturnType['getOrganisationTeams']>((organisationId) => {
    if (!organisationId || !kernel.current) return [];
    const inventory = kernel.current.getInventory();
    const inventoryTeams = Object.values(inventory.Team.registry)
      .filter((team): team is OrganisationTeamDTO => Boolean(team && team.organisationId === organisationId));
    const organisation = inventory.Organisation.registry[organisationId];
    const snapshotTeams = Array.isArray(organisation?.teams) ? organisation.teams : null;
    return mergeOrganisationTeams(inventoryTeams, snapshotTeams);
  }, [kernel]);

  const getOrganisationMembers = useCallback<useTeamsReturnType['getOrganisationMembers']>((organisationId) => {
    if (!organisationId || !kernel.current) return [];
    return Object.values(kernel.current.getInventory().User.registry)
      .filter((user): user is LeasingUserDTO => Boolean(user && user.organisationId === organisationId));
  }, [kernel]);

  const getOrganisationTeamPlans = useCallback<useTeamsReturnType['getOrganisationTeamPlans']>(async (orgId) => {
    const normalizedOrgId = orgId.trim();
    if (!normalizedOrgId) return [];
    const response = await axios.get(`/api/inventory/teams/plans?organisationId=${encodeURIComponent(normalizedOrgId)}`);
    return Array.isArray(response.data?.plans) ? response.data.plans : [];
  }, []);

  const getOrganisationTeamDetails = useCallback<useTeamsReturnType['getOrganisationTeamDetails']>(async (teamId) => {
    const normalizedTeamId = teamId.trim();
    if (!normalizedTeamId) {
      throw new Error('Team id is required.');
    }
    const response = await axios.get(`/api/inventory/teams/${normalizedTeamId}`);
    return response.data;
  }, []);

  const createOrganisationTeam = useCallback<useTeamsReturnType['createOrganisationTeam']>(async (input) => {
    const response = await axios.post('/api/inventory/teams', input);
    return response.data?.team ?? null;
  }, []);

  const addOrganisationTeamMembers = useCallback<useTeamsReturnType['addOrganisationTeamMembers']>(async (teamId, memberIds) => {
    const normalizedTeamId = teamId.trim();
    if (!normalizedTeamId || memberIds.length === 0) return null;
    const response = await axios.post(`/api/inventory/teams/${normalizedTeamId}/members`, { memberIds });
    return response.data?.team ?? null;
  }, []);

  const removeOrganisationTeamMember = useCallback<useTeamsReturnType['removeOrganisationTeamMember']>(async (teamId, memberId) => {
    const normalizedTeamId = teamId.trim();
    const normalizedMemberId = memberId.trim();
    if (!normalizedTeamId || !normalizedMemberId) return null;
    const response = await axios.delete(`/api/inventory/teams/${normalizedTeamId}/members`, { data: { memberId: normalizedMemberId } });
    return response.data?.team ?? null;
  }, []);

  const updateOrganisationTeamPlan = useCallback<useTeamsReturnType['updateOrganisationTeamPlan']>(async (teamId, input) => {
    const normalizedTeamId = teamId.trim();
    if (!normalizedTeamId) return null;
    const response = await axios.patch(`/api/inventory/teams/${normalizedTeamId}/plan`, input);
    return response.data?.teamPlan ?? null;
  }, []);

  const moveOrganisationTeamMembers = useCallback<useTeamsReturnType['moveOrganisationTeamMembers']>(async (input) => {
    const response = await axios.post('/api/inventory/teams/move-members', input);
    return response.data ?? null;
  }, []);

  const transferOrganisationTeamCredits = useCallback<useTeamsReturnType['transferOrganisationTeamCredits']>(async (input) => {
    const response = await axios.post('/api/inventory/teams/transfer-credits', input);
    if (!response.data?.fromTeam || !response.data?.toTeam) return null;
    const fromTeam = response.data.fromTeam;
    const toTeam = response.data.toTeam;
    if (response.data.dryRun === true) return response.data;
    kernel.current?.setInventorySlice('Team', (prev) => ({
      ...prev,
      registry: {
        ...prev.registry,
        [fromTeam.id]: { ...(prev.registry[fromTeam.id] ?? {}), ...fromTeam },
        [toTeam.id]: { ...(prev.registry[toTeam.id] ?? {}), ...toTeam },
      },
      lastActivity: Date.now(),
    }));
    const organisationId = typeof fromTeam.organisationId === 'string' ? fromTeam.organisationId : toTeam.organisationId;
    if (typeof organisationId === 'string' && organisationId.trim()) {
      const updatedTeams = [fromTeam, toTeam];
      kernel.current?.setInventorySlice('Organisation', (prev) => {
        const organisation = prev.registry[organisationId];
        const organisationTeams = organisation?.teams;
        if (!organisation || !Array.isArray(organisationTeams)) return prev;
        const updatedTeamById = new Map(updatedTeams.map((team) => [team.id, team]));
        let didUpdateTeamSnapshot = false;
        const teams = organisationTeams.map((team) => {
          const updatedTeam = updatedTeamById.get(team.id);
          if (!updatedTeam) return team;
          didUpdateTeamSnapshot = true;
          return { ...team, ...updatedTeam };
        });
        if (!didUpdateTeamSnapshot) return prev;
        return {
          ...prev,
          registry: {
            ...prev.registry,
            [organisationId]: { ...organisation, teams },
          },
          lastActivity: Date.now(),
        };
      });
    }
    return response.data;
  }, [kernel]);

  const deleteOrganisationTeam = useCallback<useTeamsReturnType['deleteOrganisationTeam']>(async (teamId) => {
    const normalizedTeamId = teamId.trim();
    if (!normalizedTeamId) return;
    await axios.delete(`/api/inventory/teams/${normalizedTeamId}`);
  }, []);

  const impersonateOrganisationMember = useCallback<useTeamsReturnType['impersonateOrganisationMember']>(async (userId) => {
    const normalizedUserId = userId.trim();
    if (!normalizedUserId) {
      throw new Error('Target user id is required.');
    }
    const k = kernel.current;
    if (!k) throw new Error('Kernel unavailable');
    await k.runIdentitySwitch(() => axios.post('/api/admin', { action: 'impersonateUser', targetUserId: normalizedUserId }));
    kernel.current?.clearActiveChatSession();
    kernel.current?.clearInventory();
  }, [kernel]);

  const resetOrganisationMemberPassword = useCallback<useTeamsReturnType['resetOrganisationMemberPassword']>(async (userId) => {
    const normalizedUserId = userId.trim();
    if (!normalizedUserId) {
      throw new Error('Target user id is required.');
    }
    const response = await axios.post('/api/admin', { action: 'resetUserPassword', targetUserId: normalizedUserId });
    return { newPassword: response.data?.newPassword };
  }, []);

  const setOrganisationMemberAdmin = useCallback<useTeamsReturnType['setOrganisationMemberAdmin']>(async (organisationId, userId, isOrganisationAdmin) => {
    const normalizedUserId = userId.trim();
    if (!normalizedUserId) {
      throw new Error('Target user id is required.');
    }
    await axios.post('/api/organisation/members', {
      action: 'toggleAdmin',
      organisationId,
      userIdToToggle: normalizedUserId,
      isOrganisationAdmin,
    });
    await kernel.current?.syncEntity('User', normalizedUserId);
  }, [kernel]);

  const changeOrganisationMemberEmail = useCallback<useTeamsReturnType['changeOrganisationMemberEmail']>(async (organisationId, userId, currentEmail, nextEmail) => {
    const normalizedEmail = nextEmail.trim().toLowerCase();
    if (!normalizedEmail) {
      throw new Error('Email cannot be empty.');
    }
    if (normalizedEmail === currentEmail.trim().toLowerCase()) {
      throw new Error('This is already the current email.');
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      throw new Error('Please enter a valid email address.');
    }
    await axios.post('/api/organisation/members', {
      action: 'changeEmail',
      organisationId,
      userIdToUpdateEmail: userId.trim(),
      newEmail: normalizedEmail,
    });
    return normalizedEmail;
  }, []);

  const deleteOrganisationMember = useCallback<useTeamsReturnType['deleteOrganisationMember']>(async (organisationId, userId, options) => {
    const normalizedUserId = userId.trim();
    if (!normalizedUserId) {
      throw new Error('Target user id is required.');
    }
    await axios.post('/api/organisation/members', {
      action: 'deleteUser',
      organisationId,
      userIdToDelete: normalizedUserId,
      ...options,
    });
  }, []);

  const deleteOrganisationMemberAvatar = useCallback<useTeamsReturnType['deleteOrganisationMemberAvatar']>(async (userId) => {
    const normalizedUserId = userId.trim();
    if (!normalizedUserId) {
      throw new Error('Target user id is required.');
    }
    await axios.post('/api/user/avatar/delete', { targetUserId: normalizedUserId });
    await kernel.current?.syncEntity('User', normalizedUserId);
  }, [kernel]);

  const uploadOrganisationMemberAvatar = useCallback<useTeamsReturnType['uploadOrganisationMemberAvatar']>(async (userId, file) => {
    const normalizedUserId = userId.trim();
    if (!normalizedUserId) {
      throw new Error('Target user id is required.');
    }
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please select a PNG, JPG or WebP image.');
    }
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error('File is too large. Maximum size is 5MB.');
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('targetUserId', normalizedUserId);

    const response = await fetch('/api/user/avatar/upload', { method: 'POST', body: formData });
    // Cette route répond en texte brut sur ses refus (`new NextResponse('…')`), pas en JSON :
    // `.json()` lèverait avant même de pouvoir montrer le motif du refus.
    if (!response.ok) {
      const message = await response.text().catch(() => '');
      throw new Error(message || 'Upload failed');
    }
    await kernel.current?.syncEntity('User', normalizedUserId);
  }, [kernel]);

  const setOrganisationMemberSubordinates = useCallback<useTeamsReturnType['setOrganisationMemberSubordinates']>(async (input) => {
    const trimmedTargetUsage = input.targetWeeklyUsageCredits.trim();
    const trimmedTargetGroupName = input.targetGroupName.trim();
    const targetWeeklyUsageCredits = trimmedTargetUsage.length > 0 ? Number(trimmedTargetUsage) : null;
    if (targetWeeklyUsageCredits !== null && (!Number.isFinite(targetWeeklyUsageCredits) || targetWeeklyUsageCredits < 0)) {
      throw new Error('Target usage must be a non-negative number (or empty).');
    }
    await axios.post('/api/organisation/members', {
      action: 'setSubordinates',
      organisationId: input.organisationId,
      managerUserId: input.managerUserId,
      subordinateUserIds: input.subordinateUserIds,
      targetWeeklyUsageCredits,
      targetGroupName: trimmedTargetGroupName.length > 0 ? trimmedTargetGroupName : null,
    });
  }, []);

  const setOrganisationMemberPermissions = useCallback<useTeamsReturnType['setOrganisationMemberPermissions']>(async (userId, permissions) => {
    const normalizedUserId = userId.trim();
    if (!normalizedUserId) {
      throw new Error('Target user id is required.');
    }
    await axios.post('/api/admin', {
      action: 'setUserPermissions',
      targetUserId: normalizedUserId,
      permissions,
    });
    await kernel.current?.syncEntity('User', normalizedUserId);
  }, [kernel]);

  return {
    canManageTeams,
    getOrganisationTeams,
    getOrganisationMembers,
    getOrganisationTeamPlans,
    getOrganisationTeamDetails,
    createOrganisationTeam,
    addOrganisationTeamMembers,
    removeOrganisationTeamMember,
    updateOrganisationTeamPlan,
    moveOrganisationTeamMembers,
    transferOrganisationTeamCredits,
    deleteOrganisationTeam,
    impersonateOrganisationMember,
    resetOrganisationMemberPassword,
    setOrganisationMemberAdmin,
    changeOrganisationMemberEmail,
    deleteOrganisationMember,
    deleteOrganisationMemberAvatar,
    uploadOrganisationMemberAvatar,
    setOrganisationMemberSubordinates,
    setOrganisationMemberPermissions,
  };
};
